/**
 * Document Service.
 *
 * Loan-scoped documents (WS9-1 Phase 4) go through the Vesta-direct wizard
 * trio — list / upload / download on `/api/v1/application/{loanId}/documents*`
 * (spec §2.3). Docs live ONLY in Vesta:
 *  - upload is JSON base64 (NOT multipart) with per-item `results[].error`;
 *  - download is a POST returning base64 JSON (converted to a Blob here);
 *  - delete (NP-462) ARCHIVES the document in Vesta — spec gap D1 is closed.
 *
 * Borrower-level (orphaned, no loan) documents stay on `/api/v1/borrower/me/documents*`.
 */

import { apiClient } from './api-client';
import { wizardService } from './wizard';
import type {
  DocumentAccessResponse,
  DocumentMetadata,
  DocumentUploadInput,
  DocumentUploadResponse,
} from '@/types/documents';
import type { UploadWizardDocumentItem, WizardDocument } from '@/types/wizard';
import type { components } from '@/types/api.gen';

type BorrowerDocumentSummary = components['schemas']['DocumentSummary'];
type UploadBorrowerDocumentCommand = components['schemas']['UploadBorrowerDocumentCommand'];
type UploadBorrowerDocumentResponse = components['schemas']['UploadDocumentResponse'];

/**
 * Maps borrower-facing document categories to Vesta documentTypeName values
 */
const DOCUMENT_CATEGORY_MAP: Record<string, string> = {
  // Common upload categories
  'paystub': 'Pay Stub',
  'pay_stub': 'Pay Stub',
  'Pay Stub': 'Pay Stub',
  'Paystub': 'Pay Stub',
  'w2': 'W-2',
  'w-2': 'W-2',
  'W-2': 'W-2',
  'tax_return': 'Tax Return',
  'Tax Return': 'Tax Return',
  'bank_statement': 'Bank Statement',
  'Bank Statement': 'Bank Statement',
  'id': 'Government ID',
  'driver_license': 'Driver License',
  'Driver License': 'Driver License',
  'passport': 'Passport',
  'Passport': 'Passport',
  'other': 'Other',
  'Other': 'Other',
  // Additional standard categories
  'Letter of Explanation': 'Letter of Explanation',
  'Gift Letter': 'Gift Letter',
  'Homeowners Insurance': 'Homeowners Insurance',
  'Purchase Agreement': 'Purchase Agreement',
  'Closing Disclosure': 'Closing Disclosure',
  'Loan Estimate': 'Loan Estimate',
  'Appraisal': 'Appraisal',
  'Title Report': 'Title Report',
};

/**
 * Maps a document category to the appropriate Vesta documentTypeName
 */
export function mapDocumentCategory(category?: string): string {
  if (!category) return 'Other';
  return DOCUMENT_CATEGORY_MAP[category] ?? category;
}

/**
 * Builds document upload payload with proper Vesta mapping
 */
export function buildDocumentPayload(input: DocumentUploadInput): DocumentUploadInput {
  const documentTypeName = input.documentTypeName ?? mapDocumentCategory(input.category);
  return {
    ...input,
    documentTypeName,
  };
}

/**
 * Reads a File into a plain base64 string (data-URL prefix stripped) for the
 * wizard JSON upload contract.
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const commaIndex = result.indexOf(',');
      resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error(`Failed to read file "${file.name}"`));
    reader.readAsDataURL(file);
  });
}

/** Decodes a base64 payload (wizard download response) into a Blob. */
export function base64ToBlob(contentBase64: string, contentType: string): Blob {
  const binary = atob(contentBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: contentType || 'application/octet-stream' });
}

/**
 * The generated types give every int64 field `number | string` — the OpenAPI
 * schema permits a numeric string for them — so a size has to be coerced rather
 * than type-checked, or a stringified value is silently dropped. Shared with the
 * task and BP-document normalizers, which read the same int64 fields.
 */
export function toNumberOrUndefined(value: number | string | null | undefined): number | undefined {
  if (value == null) return undefined;
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : undefined;
}

/**
 * Maps a borrower-level `DocumentSummary` (GET/POST `/api/v1/borrower/me/documents`,
 * WS9-1 Phase 5) to the UI-facing DocumentMetadata shape.
 */
export function mapDocumentSummaryToMetadata(summary: BorrowerDocumentSummary): DocumentMetadata {
  const file = summary.latestFile ?? undefined;
  return {
    id: summary.id,
    loanId: summary.loanId ?? '',
    documentId: summary.id,
    name: file?.fileName ?? summary.documentTypeName ?? undefined,
    category: summary.documentTypeCategory ?? summary.documentTypeName ?? undefined,
    documentTypeName: summary.documentTypeName ?? undefined,
    status: summary.status ?? 'Pending',
    createdAt: summary.createdAt ?? undefined,
    updatedAt: summary.updatedAt ?? undefined,
    uploadedAt: file?.uploadedAt ?? summary.createdAt ?? undefined,
    fileId: file?.id,
    fileName: file?.fileName ?? undefined,
    fileSize: toNumberOrUndefined(file?.sizeBytes),
    size: toNumberOrUndefined(file?.sizeBytes),
    contentType: file?.contentType ?? undefined,
    uploadedBy: file?.uploadedBy ?? undefined,
  };
}

/**
 * NP-423: borrower-facing "Uploaded by" label.
 *
 * The API's vocabulary for this field is CLOSED — verified against
 * afn-path-api @ 13579db (read-only):
 *
 *  - loan/wizard documents (`GET /api/v1/application/{loanId}/documents`) send a
 *    required, non-nullable role word: `"Borrower"` or, since NP-550, `"Co-Borrower"`
 *    when PATH performed the upload (attribution row, `UploaderFor`) or `"Borrower"`
 *    when Vesta stamped `UploadSource=Pos`, else `"LO"`
 *    — `WizardDocuments.cs:106,234,247`;
 *  - borrower-level documents (`GET /api/v1/borrower/me/documents`) send the
 *    uploader's EMAIL, and it is always the signed-in borrower's own address:
 *    `UploadBorrowerDocumentHandler.cs:26` passes `currentBorrower.Email`;
 *  - the generated URLA 1003 sends the literal `"System"` —
 *    `Urla1003DocumentService.cs:124`.
 *
 * Anything outside that vocabulary means the contract moved. This function
 * deliberately does NOT guess a role for such values: guessing is what produced
 * QA's "Uploaded by LO" on a borrower-uploaded document (the API's own
 * `MapUploadedBy` fallback mapped "no upload source at all" to a confident
 * "LO"), and the same page has already been bitten by a silent map default in
 * NP-427/428. An unrecognized value therefore hides the label — never a wrong
 * attribution — and warns once so it is diagnosable instead of invisible.
 */
// Returns 'Borrower' | 'Co-Borrower' | 'LO' | 'System', or undefined when absent
// OR unrecognized. Typed as plain string|undefined: unioning literals with
// `string` collapses anyway.
const warnedUploadedByValues = new Set<string>();

export function uploadedByLabel(value?: string | null): string | undefined {
  const v = (value ?? '').trim();
  if (!v) return undefined;
  const lower = v.toLowerCase();
  if (lower === 'system') return 'System';
  // NP-550 AC-5: the API writes "Co-Borrower" (WizardDocumentService.CoBorrowerUploader).
  // The unhyphenated form is accepted too because the API's INTERNAL role vocabulary
  // (LoanBorrowers.Role) spells it "CoBorrower", and either reaching this field means the
  // same person — an unrecognized value hides the label entirely (see below), which on a
  // co-borrower's own document is the failure this AC exists to fix.
  if (lower === 'co-borrower' || lower === 'coborrower') return 'Co-Borrower';
  if (lower === 'borrower') return 'Borrower';
  if (lower === 'lo' || lower === 'loan officer' || lower === 'loanofficer') return 'LO';
  // Borrower-level documents carry the uploader's email, and the API only ever
  // writes the signed-in borrower's own address there (see the handler cited
  // above), so this is a contract fact rather than an inference. It is scoped to
  // addresses precisely so it cannot swallow an unknown role word.
  if (v.includes('@')) return 'Borrower';
  // Outside the closed vocabulary: report it, don't render it. A raw passthrough
  // here used to put wire values straight into borrower-facing copy
  // ("Uploaded by Pos").
  if (!warnedUploadedByValues.has(lower)) {
    warnedUploadedByValues.add(lower);
    console.warn(
      `[NP-423] Unrecognized document uploadedBy value ${JSON.stringify(v)} — ` +
        'hiding the "Uploaded by" label rather than guessing a role. ' +
        'If the API added a new uploader kind, extend uploadedByLabel().',
    );
  }
  return undefined;
}

/**
 * NP-550: which role the VIEWER holds on the loan they are looking at. The uploader
 * attribution is a role word, so "did I upload this?" is a comparison against the
 * viewer's own role — see `documentDeleteUnavailableReason`.
 */
export type DocumentViewerRole = 'Borrower' | 'Co-Borrower';

/**
 * NP-462: may the borrower remove THIS document?
 *
 * The API allows a borrower to remove only their own PATH uploads and returns 403
 * for anything else, so the UI must not offer the action where it would fail. The
 * frontend already knows the answer per row: NP-423 puts the uploader attribution
 * on every document.
 *
 * NP-550 AC-8: `viewer` is what makes "their own" mean the right thing for a
 * co-borrower. It defaults to `'Borrower'` — the pre-NP-550 behaviour — so a caller
 * with no loan role to offer (there is no role without a loan) is unaffected.
 *
 * Fails CLOSED, matching the API: an absent or unrecognized uploader is not proof
 * of ownership, so the action stays unavailable rather than 403-ing on click.
 * Returns the reason it is unavailable, or `undefined` when it is allowed.
 */
export function documentDeleteUnavailableReason(
  doc: DocumentMetadata,
  viewer: DocumentViewerRole = 'Borrower',
): string | undefined {
  return isOwnUpload(doc, viewer)
    ? undefined
    : "Only documents you uploaded can be removed. Ask your loan officer to remove this one.";
}

/**
 * NP-550: did the signed-in borrower upload this document?
 *
 * An email is treated as the viewer's own whichever role they hold: only
 * borrower-level (orphaned) documents carry one, and the API writes the signed-in
 * borrower's own address there — so matching it against a role word would deny a
 * co-borrower their own upload on a contract detail that has nothing to say about
 * roles.
 */
function isOwnUpload(doc: DocumentMetadata, viewer: DocumentViewerRole): boolean {
  const raw = (doc.uploadedBy ?? '').trim();
  if (raw.includes('@')) return true;
  return uploadedByLabel(raw) === viewer;
}

/**
 * Maps a wizard (Vesta) document to the UI-facing DocumentMetadata shape.
 * Multi-file documents emit one row per file (unique row `id`, shared
 * `documentId`); `fileId` targets the exact file on download.
 */
export function mapWizardDocumentToMetadata(loanId: string, doc: WizardDocument): DocumentMetadata[] {
  const docId = doc.id ?? '';
  const base: Omit<DocumentMetadata, 'id'> = {
    loanId,
    documentId: docId || undefined,
    name: doc.name ?? undefined,
    category: doc.documentType ?? undefined,
    documentTypeName: doc.documentType ?? undefined,
    status: doc.status ?? 'Pending',
    uploadedAt: doc.uploadedAt ?? undefined,
    createdAt: doc.uploadedAt ?? undefined,
    updatedAt: doc.uploadedAt ?? undefined,
    fileName: doc.name ?? undefined,
    // NP-423: the wire doesn't carry attribution for Vesta loan docs YET
    // (QA bounce 2026-07-27) — map it now so the label lights up the moment
    // the API adds uploadedBy (doc- or file-level).
    uploadedBy: doc.uploadedBy ?? undefined,
  };

  const files = doc.files ?? [];
  if (files.length === 0) {
    return [{ ...base, id: docId }];
  }

  return files.map((file) => ({
    ...base,
    id: files.length > 1 ? `${docId}::${file.id}` : docId || file.id,
    fileId: file.id,
    fileName: file.name ?? doc.name ?? undefined,
    fileSize: toNumberOrUndefined(file.fileSize),
    size: toNumberOrUndefined(file.fileSize),
    uploadedBy: file.uploadedBy ?? doc.uploadedBy ?? undefined,
  }));
}

export const documentService = {
  /** Get all documents for a loan — Vesta-backed wizard list. */
  async getDocuments(loanId: string): Promise<DocumentMetadata[]> {
    const result = await wizardService.documents.list(loanId);
    return (result.documents ?? []).flatMap((doc) => mapWizardDocumentToMetadata(loanId, doc));
  },

  /**
   * Upload a document — wizard JSON base64 upload (NOT multipart).
   * Partial failures come back HTTP 200 with per-item `results[].error`,
   * so each result is checked and surfaced as a thrown Error.
   */
  async uploadDocument(
    loanId: string,
    file: File,
    metadata: DocumentUploadInput,
    signal?: AbortSignal,
  ): Promise<DocumentUploadResponse> {
    const payload = buildDocumentPayload(metadata);
    const item: UploadWizardDocumentItem = {
      fileName: file.name,
      fileContentBase64: await fileToBase64(file),
      documentType: payload.documentTypeName ?? 'Other',
      documentRequiredTaskId: payload.documentRequiredTaskIds?.[0] ?? null,
      // NP-488: the server matches this against its own option list, so only the id
      // travels — the entity type is never taken from the client. Omitted rather than
      // sent as null when there is no choice to report (Copilot).
      ...(payload.associatedEntities?.[0]?.entityId
        ? { associatedEntityId: payload.associatedEntities[0].entityId }
        : {}),
    };
    const response = await wizardService.documents.upload(loanId, [item], { signal });
    const result = response.results?.find((r) => r.fileName === file.name) ?? response.results?.[0];
    if (!result) {
      throw new Error(`Upload failed for "${file.name}": no result returned.`);
    }
    if (result.error) {
      throw new Error(result.error);
    }
    return {
      documentId: result.documentId ?? '',
      id: result.documentId ?? undefined,
      fileName: result.fileName,
    };
  },

  /** Object-URL access for preview — built from the wizard base64 download. */
  async getDocumentAccess(loanId: string, documentId: string, fileId?: string): Promise<DocumentAccessResponse> {
    const result = await wizardService.documents.download(loanId, { documentId, fileId });
    const blob = base64ToBlob(result.contentBase64, result.contentType);
    return {
      url: URL.createObjectURL(blob),
      expiresAt: new Date(Date.now() + 5 * 60_000).toISOString(),
      contentType: blob.type,
      fileName: result.fileName,
    };
  },

  /** Download a document as Blob — wizard POST returning base64 JSON. */
  async downloadDocument(loanId: string, documentId: string, fileId?: string): Promise<Blob> {
    const result = await wizardService.documents.download(loanId, { documentId, fileId });
    return base64ToBlob(result.contentBase64, result.contentType);
  },

  /**
   * NP-462: remove a loan document the borrower uploaded.
   *
   * The API ARCHIVES it in Vesta rather than destroying it (Vesta exposes no hard
   * delete for loan documents), so the audit trail underwriting needs survives and
   * the document simply leaves the borrower's list. Authorization is server-side:
   * only the borrower's own PATH uploads qualify — an LO/staff upload returns 403,
   * an unknown document or a loan the borrower doesn't own returns 404.
   *
   * REQUIRES afn-path-api PR #54 to be deployed. Callers gate on
   * `documentDeleteUnavailableReason(doc)` — per DOCUMENT, not per loan — so the
   * control is never offered where this would 403.
   */
  deleteDocument(loanId: string, documentId: string): Promise<void> {
    return apiClient.delete<void>(`/api/v1/application/${loanId}/documents/${documentId}`);
  },

  // ── Borrower-level (orphaned) document operations ───────────

  /** Get orphaned documents for the authenticated borrower (no loan) */
  async getBorrowerDocuments(): Promise<DocumentMetadata[]> {
    const result = await apiClient.get<{ documents: BorrowerDocumentSummary[] }>('/api/v1/borrower/me/documents');
    return (result.documents ?? []).map(mapDocumentSummaryToMetadata);
  },

  /**
   * Upload a document at borrower level (orphaned — no loan required).
   * JSON base64 body (`UploadBorrowerDocumentCommand`, WS9-1 Phase 5 — the API
   * dropped the old multipart contract, mirroring the wizard-trio upload shape).
   */
  async uploadBorrowerDocument(
    file: File,
    metadata: DocumentUploadInput,
    signal?: AbortSignal,
  ): Promise<DocumentUploadResponse> {
    const payload = buildDocumentPayload(metadata);
    const command: UploadBorrowerDocumentCommand = {
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
      contentBase64: await fileToBase64(file),
      documentTypeName: payload.documentTypeName ?? 'Other',
      documentTypeCategory: payload.category ?? null,
    };
    const response = await apiClient.post<UploadBorrowerDocumentResponse>(
      '/api/v1/borrower/me/documents',
      command,
      { signal },
    );
    const doc = response.document;
    return {
      documentId: doc?.id ?? '',
      id: doc?.id ?? undefined,
      fileName: doc?.latestFile?.fileName ?? file.name,
      uploadedAt: doc?.latestFile?.uploadedAt ?? doc?.createdAt ?? undefined,
    };
  },

  /** Delete an orphaned borrower document */
  deleteBorrowerDocument(documentId: string): Promise<void> {
    return apiClient.delete<void>(`/api/v1/borrower/me/documents/${documentId}`);
  },
};
